import { Elements } from '@stripe/react-stripe-js';
import { useCallback, useRef } from 'react';
import { NativeTypes } from 'react-dnd-html5-backend';
import { useDispatch } from 'react-redux';

import {
  Box,
  Button,
  Collapse,
  FileInlineCard,
  FileInlineCardIcon,
  FileInlineCardLabel,
  FileProvider,
  Flex,
  FormControl,
  FormLabel,
  IconButton,
  Input,
  Radio,
  RadioGroup,
  Text,
  Tooltip,
  TrashIcon,
  UploadDiskIcon,
} from '@frontend/legacy-components';

import { useDrop } from 'components/customDragLayer';

import { PAYMENT_METHOD, SHIPPING_TYPE } from 'constants/all';

import { useAppSelector } from 'store/hooks';
import { getOrder } from 'store/orders/selectors';
import { hasCreditCardCheckoutEnabled } from 'store/session/selectors';

import { useExtractFilesFromNativeFileBundleForUpload } from 'utils/hooks';
import { stripePromise } from 'utils/stripePromise';

import { DRAWER_MODES } from '../constants';
import {
  setCardDetails,
  setExpandedCustomerInfoStepIndices,
  setPaymentMethod,
  setPurchaseOrderFile,
  setPurchaseOrderNumber,
  setShippingType,
} from '../data/actions';
import {
  getAccount,
  getBillingInformation,
  getDrawerMode,
  getExpandedCustomerInfoStepIndices,
  getPaymentMethod,
  getPurchaseOrderFile,
  getPurchaseOrderNumber,
  getShippingType,
} from '../data/reducer';
import type { PaymentMethod as PaymentMethodType } from '../data/types';
import { CreditCardInput } from './CreditCardInput';

export const PaymentMethod = () => {
  const dispatch = useDispatch();
  const paymentMethod = useAppSelector(getPaymentMethod) ?? 'none';
  const poNumber = useAppSelector(getPurchaseOrderNumber);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const poFile = useAppSelector(getPurchaseOrderFile);
  const drawerMode = useAppSelector(getDrawerMode);
  const shippingType = useAppSelector(getShippingType);
  const account = useAppSelector(getAccount);
  const isCreditCardCheckoutEnabled = useAppSelector(
    hasCreditCardCheckoutEnabled,
  );
  const billingInformation = useAppSelector(getBillingInformation);
  const expandedCustomerInfoStepIndices = useAppSelector(
    getExpandedCustomerInfoStepIndices,
  );
  const order = useAppSelector(getOrder);

  const isPaymentMethodDisabled = drawerMode === DRAWER_MODES.EDIT;
  const showReplacePoFile =
    drawerMode === DRAWER_MODES.EDIT && !!order?.poFileUrl;

  const setPaymentMethodData = useCallback(
    (paymentMethod: PaymentMethodType) => {
      dispatch(setPaymentMethod(paymentMethod));
      if (paymentMethod !== PAYMENT_METHOD.PURCHASE_ORDER) {
        if (poNumber) {
          dispatch(setPurchaseOrderNumber(undefined));
        }
        if (poFile) {
          dispatch(setPurchaseOrderFile(undefined));
        }
        if (
          shippingType === SHIPPING_TYPE.SUPPLIER_ACCOUNT ||
          shippingType === SHIPPING_TYPE.CUSTOMER_ACCOUNT
        ) {
          dispatch(setShippingType({ shippingType: undefined }));
        }
      }
      if (paymentMethod !== PAYMENT_METHOD.CREDIT_CARD) {
        dispatch(setCardDetails({ stripeToken: undefined, card: undefined }));
        if (shippingType === SHIPPING_TYPE.CUSTOMER_ACCOUNT) {
          dispatch(setShippingType({ shippingType: undefined }));
        }
      }
      if (paymentMethod === PAYMENT_METHOD.CREDIT_CARD && !billingInformation) {
        const nextIndices = expandedCustomerInfoStepIndices.includes(1)
          ? expandedCustomerInfoStepIndices
          : [...expandedCustomerInfoStepIndices, 1];
        dispatch(
          setExpandedCustomerInfoStepIndices({
            expandedCustomerInfoStepIndices: nextIndices,
          }),
        );
      }
    },
    [
      billingInformation,
      expandedCustomerInfoStepIndices,
      poFile,
      poNumber,
      shippingType,
      dispatch,
    ],
  );

  const handleDropPoFile = useExtractFilesFromNativeFileBundleForUpload({
    onExtractFiles: (files) => {
      dispatch(setPurchaseOrderFile(files[0]));
    },
  });

  const [{ canDrop, isOver }, dropzoneRef] = useDrop({
    accept: NativeTypes.FILE,
    collect: (monitor) => ({
      canDrop: monitor.canDrop(),
      isOver: monitor.isOver(),
    }),
    onDropEnd: (item) => {
      handleDropPoFile({ nativeFileBundle: item });
      return undefined;
    },
  });

  return (
    <FormControl>
      <FormLabel>Payment Method</FormLabel>
      <RadioGroup
        value={paymentMethod}
        onChange={(value) =>
          value === 'none'
            ? setPaymentMethodData(PAYMENT_METHOD.NONE)
            : setPaymentMethodData(value)
        }
        isDisabled={isPaymentMethodDisabled}
        alignItems="start"
        width="100%"
        sx={{
          '.chakra-collapse': {
            alignSelf: 'stretch',
          },
        }}
      >
        <Radio value="none">Not Provided</Radio>
        <Tooltip
          placement="right"
          label={
            !account && !isPaymentMethodDisabled
              ? 'Contact must be associated with an account to set payment method to Purchase Order'
              : null
          }
          shouldWrapChildren={true}
        >
          <Radio
            value={PAYMENT_METHOD.PURCHASE_ORDER}
            isDisabled={isPaymentMethodDisabled || !account}
          >
            Purchase Order
          </Radio>
        </Tooltip>
        <Collapse in={paymentMethod === PAYMENT_METHOD.PURCHASE_ORDER}>
          <Flex pl={3} pb={1} gap={1.5} w="100%">
            <FormControl isRequired flex="0 0 fit-content">
              <FormLabel>Purchase Order #</FormLabel>
              <Input
                defaultValue={poNumber}
                onBlur={(event) =>
                  dispatch(
                    setPurchaseOrderNumber(event.target.value || undefined),
                  )
                }
              />
            </FormControl>
            <FormControl flex="1 1 fit-content" minWidth={0}>
              <FormLabel>Purchase Order File</FormLabel>
              {poFile ? (
                <Flex h={4} gap={1} alignItems="center">
                  <FileProvider
                    filename={poFile.name}
                    fileType={poFile.type}
                    fileSize={poFile.size}
                    uploaded=""
                    isReadOnly={true}
                  >
                    <FileInlineCard>
                      <FileInlineCardIcon />
                      <FileInlineCardLabel />
                    </FileInlineCard>
                  </FileProvider>
                  <IconButton
                    onClick={() => dispatch(setPurchaseOrderFile(undefined))}
                    flexShrink={0}
                  >
                    <TrashIcon />
                  </IconButton>
                </Flex>
              ) : (
                <Flex
                  ref={dropzoneRef}
                  justify="center"
                  align="center"
                  py={0.75}
                  px={1.5}
                  gap={1}
                  h={4}
                  border="1px dashed"
                  borderColor={
                    canDrop
                      ? isOver
                        ? 'interaction.main'
                        : 'interaction.hover'
                      : 'separator.border'
                  }
                  borderRadius="md"
                  backgroundColor={
                    canDrop
                      ? isOver
                        ? 'interaction.surfaceActive'
                        : 'interaction.overlayHover'
                      : ''
                  }
                >
                  <UploadDiskIcon
                    my={1}
                    mx={0.5}
                    boxSize={3}
                    color="feature.placeholder"
                  />
                  <Text
                    display="flex"
                    fontWeight="500"
                    fontSize="sm"
                    lineHeight="20px"
                  >
                    Drag and drop or&nbsp;
                    <Box>
                      <Input
                        ref={fileInputRef}
                        display="none"
                        type="file"
                        onChange={(event) =>
                          dispatch(
                            setPurchaseOrderFile(
                              event.target.files?.[0] ?? undefined,
                            ),
                          )
                        }
                      />
                      <Button
                        variant="link"
                        size="sm"
                        onClick={() => fileInputRef.current?.click()}
                      >
                        choose file
                      </Button>
                    </Box>
                    &nbsp;to {showReplacePoFile ? 'replace' : 'upload'} PO
                  </Text>
                </Flex>
              )}
            </FormControl>
          </Flex>
        </Collapse>
        <Radio
          value={PAYMENT_METHOD.CREDIT_CARD}
          isDisabled={isPaymentMethodDisabled || !isCreditCardCheckoutEnabled}
        >
          Credit Card
        </Radio>
        <Collapse in={paymentMethod === PAYMENT_METHOD.CREDIT_CARD}>
          <Box pl={3}>
            <Elements stripe={stripePromise}>
              <CreditCardInput />
            </Elements>
          </Box>
        </Collapse>
      </RadioGroup>
    </FormControl>
  );
};
